import { useState, useEffect } from 'react';
import { getLearningProgressAnalytics } from '../../services/learningProgressService';
import BarChart from '../charts/BarChart';

const LearningProgressTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getLearningProgressAnalytics(30);
        setProgressData(data);
      } catch (error) {
        console.error('Error loading learning progress data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading learning progress analytics...</p>
      </div>
    );
  }

  if (!progressData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No learning progress data available</p>
      </div>
    );
  }

  // Topic completion rates chart
  const completionRatesData = {
    labels: progressData.topicCompletionMetrics.slice(0, 15).map((t: any) => t.topicTitle.substring(0, 20)),
    datasets: [
      {
        label: 'Completion Rate (%)',
        data: progressData.topicCompletionMetrics.slice(0, 15).map((t: any) => t.completionRate),
        backgroundColor: '#10b981',
      },
    ],
  };

  // Quiz success rates
  const quizKeys = Array.from(progressData.quizSuccessRates.keys()).slice(0, 10);
  const quizValues = Array.from(progressData.quizSuccessRates.values()).slice(0, 10);
  const quizSuccessData = {
    labels: quizKeys as string[],
    datasets: [
      {
        label: 'Success Rate (%)',
        data: quizValues as number[],
        backgroundColor: '#2563eb',
      },
    ],
  };

  // Exercise success rates
  const exerciseKeys = Array.from(progressData.exerciseSuccessRates.keys()).slice(0, 10);
  const exerciseValues = Array.from(progressData.exerciseSuccessRates.values()).slice(0, 10);
  const exerciseSuccessData = {
    labels: exerciseKeys as string[],
    datasets: [
      {
        label: 'Success Rate (%)',
        data: exerciseValues as number[],
        backgroundColor: '#f59e0b',
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Learning Progress Deep Dive
      </h1>

      {/* Overall Metrics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h4 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#666' }}>Overall Accuracy</h4>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2563eb' }}>
            {(progressData.overallAccuracy * 100).toFixed(1)}%
          </div>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h4 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#666' }}>Completion Rate</h4>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
            {(progressData.completionRate * 100).toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Performance Over Time Charts */}
      <h2 style={{ marginTop: '2rem', marginBottom: '1rem', fontSize: '1.5rem' }}>
        Performance Over Time
      </h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Modules Performance</h3>
          {progressData.modulesPerformanceOverTime && progressData.modulesPerformanceOverTime.length > 0 ? (
            <BarChart data={{
              labels: progressData.modulesPerformanceOverTime.map((p: any) => p.week),
              datasets: [{
                label: 'Average Accuracy (%)',
                data: progressData.modulesPerformanceOverTime.map((p: any) => p.average_accuracy * 100),
                backgroundColor: '#2563eb',
              }],
            }} height={300} />
          ) : (
            <p>No module performance data available</p>
          )}
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Exercises Performance</h3>
          {progressData.exercisesPerformanceOverTime && progressData.exercisesPerformanceOverTime.length > 0 ? (
            <BarChart data={{
              labels: progressData.exercisesPerformanceOverTime.map((p: any) => p.week),
              datasets: [{
                label: 'Average Accuracy (%)',
                data: progressData.exercisesPerformanceOverTime.map((p: any) => p.average_accuracy * 100),
                backgroundColor: '#f59e0b',
              }],
            }} height={300} />
          ) : (
            <p>No exercise performance data available</p>
          )}
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Quizzes Performance</h3>
          {progressData.quizzesPerformanceOverTime && progressData.quizzesPerformanceOverTime.length > 0 ? (
            <BarChart data={{
              labels: progressData.quizzesPerformanceOverTime.map((p: any) => p.week),
              datasets: [{
                label: 'Average Accuracy (%)',
                data: progressData.quizzesPerformanceOverTime.map((p: any) => p.average_accuracy * 100),
                backgroundColor: '#8b5cf6',
              }],
            }} height={300} />
          ) : (
            <p>No quiz performance data available</p>
          )}
        </div>
      </div>

      {/* Charts */}
      <h2 style={{ marginTop: '2rem', marginBottom: '1rem', fontSize: '1.5rem' }}>
        Success Rates by Content Type
      </h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Topic Completion Rates</h3>
          <BarChart data={completionRatesData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Quiz Success Rates</h3>
          <BarChart data={quizSuccessData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Exercise Success Rates</h3>
          <BarChart data={exerciseSuccessData} height={300} />
        </div>
      </div>

      {/* Topic Completion Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        marginBottom: '2rem',
      }}>
        <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
          Topic Completion Metrics
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Topic</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Category</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Completion Rate</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Avg Time (min)</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Completions</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Total Attempts</th>
            </tr>
          </thead>
          <tbody>
            {progressData.topicCompletionMetrics.slice(0, 30).map((topic: any, index: number) => (
              <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '1rem' }}>{topic.topicTitle}</td>
                <td style={{ padding: '1rem' }}>{topic.category}</td>
                <td style={{ padding: '1rem', color: topic.completionRate > 50 ? '#10b981' : '#ef4444' }}>
                  {topic.completionRate.toFixed(1)}%
                </td>
                <td style={{ padding: '1rem' }}>
                  {topic.averageTimeToComplete > 0 ? topic.averageTimeToComplete.toFixed(0) : 'N/A'}
                </td>
                <td style={{ padding: '1rem' }}>{topic.completions}</td>
                <td style={{ padding: '1rem' }}>{topic.totalAttempts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Weak Areas */}
      {progressData.weakAreas && progressData.weakAreas.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          marginBottom: '2rem',
        }}>
          <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
            Areas Needing Improvement
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Topic</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Overall Accuracy</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Weak Modules</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Weak Exercises</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Weak Quizzes</th>
              </tr>
            </thead>
            <tbody>
              {progressData.weakAreas.map((area: any, index: number) => (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '1rem' }}>{area.topic}</td>
                  <td style={{ padding: '1rem', color: area.overall_accuracy < 0.5 ? '#ef4444' : '#f59e0b' }}>
                    {(area.overall_accuracy * 100).toFixed(1)}%
                  </td>
                  <td style={{ padding: '1rem' }}>{area.modules_count || 0}</td>
                  <td style={{ padding: '1rem' }}>{area.exercises_count || 0}</td>
                  <td style={{ padding: '1rem' }}>{area.quizzes_count || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Practice Plan Suggestions */}
      {progressData.practicePlanSuggestions && progressData.practicePlanSuggestions.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          marginBottom: '2rem',
        }}>
          <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
            Personalized Practice Plan Suggestions
          </h3>
          <div style={{ padding: '1rem' }}>
            {progressData.practicePlanSuggestions.map((plan: any, index: number) => (
              <div key={index} style={{
                padding: '1rem',
                marginBottom: '1rem',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: plan.priority === 'high' ? '#fef2f2' : '#f9fafb',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    backgroundColor: plan.priority === 'high' ? '#ef4444' : '#3b82f6',
                    color: 'white',
                    marginRight: '0.5rem',
                  }}>
                    {plan.priority.toUpperCase()}
                  </span>
                  <h4 style={{ margin: 0 }}>{plan.topic}</h4>
                </div>
                <p style={{ margin: '0.5rem 0', color: '#666' }}>{plan.reason}</p>
                <div style={{ marginTop: '0.5rem' }}>
                  <strong>Recommended Actions:</strong>
                  <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                    {plan.recommended_actions.map((action: string, i: number) => (
                      <li key={i} style={{ marginBottom: '0.25rem' }}>{action}</li>
                    ))}
                  </ul>
                </div>
                <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
                  Estimated time: {plan.estimated_time_minutes} minutes
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stuck Users Table */}
      {progressData.stuckUsers.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}>
          <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
            Stuck Users (No Progress in 7+ Days)
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>User ID</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Topic</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Last Progress</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Days Since Update</th>
              </tr>
            </thead>
            <tbody>
              {progressData.stuckUsers.map((user: any, index: number) => (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', wordBreak: 'break-all' }}>
                    {user.userId.substring(0, 20)}...
                  </td>
                  <td style={{ padding: '1rem' }}>{user.topicTitle}</td>
                  <td style={{ padding: '1rem' }}>{user.lastProgress.toFixed(1)}%</td>
                  <td style={{ padding: '1rem', color: '#ef4444' }}>{user.daysSinceUpdate} days</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LearningProgressTab;

